import React, { useState } from 'react';
import { X, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { services } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QuoteForm = ({ onClose, preSelectedService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: preSelectedService,
    address: '',
    details: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/quotes`, formData);
      toast.success('Demande de devis envoyée! Nous vous contacterons rapidement avec une estimation.', {
        duration: 4000
      });
      onClose();
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erreur lors de l\'envoi de la demande. Veuillez réessayer.', {
        duration: 4000
      });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-yellow-500 to-yellow-400 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-gray-900" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Demande de Devis</h2>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            disabled={loading}
            className="text-gray-900 hover:bg-white/20"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="quote-name" className="text-gray-700 font-semibold mb-2 block">
                Nom complet *
              </Label>
              <Input
                id="quote-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="Jean Dupont"
              />
            </div>
            <div>
              <Label htmlFor="quote-phone" className="text-gray-700 font-semibold mb-2 block">
                Téléphone *
              </Label>
              <Input
                id="quote-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                disabled={loading}
                className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
                placeholder="06 12 34 56 78"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="quote-email" className="text-gray-700 font-semibold mb-2 block">
              Email *
            </Label>
            <Input
              id="quote-email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
              placeholder="jean.dupont@example.com"
            />
          </div>

          <div>
            <Label htmlFor="quote-service" className="text-gray-700 font-semibold mb-2 block">
              Service souhaité *
            </Label>
            <Select required value={formData.service} onValueChange={handleServiceChange} disabled={loading}>
              <SelectTrigger className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500">
                <SelectValue placeholder="Sélectionnez un service" />
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.title}>
                    {service.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="quote-address" className="text-gray-700 font-semibold mb-2 block">
              Adresse d'intervention *
            </Label>
            <Input
              id="quote-address"
              name="address"
              type="text"
              required
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
              className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500"
              placeholder="Adresse complète en Alsace"
            />
          </div>

          <div>
            <Label htmlFor="quote-details" className="text-gray-700 font-semibold mb-2 block">
              Détails du projet
            </Label>
            <Textarea
              id="quote-details"
              name="details"
              value={formData.details}
              onChange={handleChange}
              disabled={loading}
              rows={5}
              className="w-full border-gray-300 focus:border-yellow-500 focus:ring-yellow-500 resize-none"
              placeholder="Décrivez votre besoin en détail (surface, état, particularités...)"
            />
          </div>

          <div className="flex gap-4">
            <Button 
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1 border-2 border-gray-300 text-gray-700 font-semibold py-6"
            >
              Annuler
            </Button>
            <Button 
              type="submit"
              disabled={loading}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold py-6 shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              <FileText className="w-5 h-5 mr-2" />
              {loading ? 'Envoi...' : 'Envoyer la demande'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;
